import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  LayoutDashboard,
  Shield,
  Image,
  Sparkles,
  Users,
  Settings,
  FileText,
  LogOut,
  ChevronDown,
  Bell,
  Menu,
  X,
  MessageSquare,
  MessageSquareText,
  Zap,
  Trash2,
  UserPlus,
  Smartphone,
  UserCheck,
  Download,
} from 'lucide-react';
import { useState } from 'react';

type MinRole = 'USER' | 'MODERATOR' | 'ADMIN';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  minRole?: MinRole; // Minimum role required to see this item (default: USER)
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: <LayoutDashboard size={20} />, minRole: 'MODERATOR' },
  { path: '/moderation', label: 'Moderation', icon: <Shield size={20} />, minRole: 'MODERATOR' },
  { path: '/prompts', label: 'Prompts', icon: <MessageSquare size={20} />, adminOnly: true, minRole: 'ADMIN' },
  { path: '/automation', label: 'Automation', icon: <Zap size={20} />, adminOnly: true, minRole: 'ADMIN' },
  { path: '/frames', label: 'Frames', icon: <Image size={20} />, adminOnly: true, minRole: 'ADMIN' },
  { path: '/loading', label: 'Loading Screens', icon: <Smartphone size={20} />, adminOnly: true, minRole: 'ADMIN' },
  { path: '/banners', label: 'Banner Messages', icon: <Bell size={20} />, adminOnly: true, minRole: 'ADMIN' },
  { path: '/users', label: 'Users', icon: <Users size={20} />, minRole: 'ADMIN' },
  { path: '/feedback', label: 'Feedback', icon: <MessageSquareText size={20} />, minRole: 'MODERATOR' },
  { path: '/logs', label: 'Audit Logs', icon: <FileText size={20} />, minRole: 'ADMIN' },
  { path: '/testers', label: 'Testers', icon: <UserPlus size={20} />, minRole: 'ADMIN' },
  { path: '/filters', label: 'Filters', icon: <Sparkles size={20} />, adminOnly: true, minRole: 'ADMIN' },
  { path: '/verification', label: 'Age Verification', icon: <UserCheck size={20} />, minRole: 'MODERATOR' },
  { path: '/system', label: 'System', icon: <Settings size={20} />, adminOnly: true, minRole: 'ADMIN' },
  { path: '/data-export', label: 'Export My Data', icon: <Download size={20} /> },
  { path: '/delete-account', label: 'Delete Account', icon: <Trash2 size={20} /> },
];

export function AdminLayout() {
  const { user, role, logout, isAdmin, isModerator } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Filter nav items based on role hierarchy: ADMIN > MODERATOR > USER
  const visibleNavItems = navItems.filter((item) => {
    const minRole = item.minRole || 'USER';
    if (minRole === 'ADMIN') return isAdmin;
    if (minRole === 'MODERATOR') return isModerator;
    return true; // USER level - everyone sees it
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:static lg:inset-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">R</span>
              </div>
              <span className="text-white font-semibold">RightNow Admin</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`
                }
              >
                <>{item.icon}</>
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-gray-800">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.signInDetails?.loginId?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.signInDetails?.loginId || 'Admin'}
                </p>
                <p className="text-xs text-gray-400">{role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-2.5 mt-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <LogOut size={20} />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {getPageTitle(location.pathname)}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Environment badge + version */}
            <div className="hidden sm:flex items-center gap-2">
              {import.meta.env.MODE !== 'production' && (
                <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700 border border-amber-300 uppercase tracking-wide">
                  {import.meta.env.MODE}
                </span>
              )}
              <span className="text-xs text-gray-400">v{__APP_VERSION__}</span>
            </div>

            {/* Notifications (placeholder) */}
            <button className="relative text-gray-600 hover:text-gray-900">
              <Bell size={20} />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-danger-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                3
              </span>
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <span className="hidden sm:inline">{user?.signInDetails?.loginId}</span>
                <ChevronDown size={16} />
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.signInDetails?.loginId}
                      </p>
                      <p className="text-xs text-gray-500">{role}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function getPageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    '/': 'Dashboard',
    '/moderation': 'Moderation Queue',
    '/verification': 'Age Verification Queue',
    '/prompts': 'Prompt Suggestions',
    '/automation': 'Automation',
    '/frames': 'Frame Management',
    '/filters': 'Filter Management',
    '/loading': 'Loading Screens',
    '/banners': 'Banner Messages',
    '/users': 'User Management',
    '/system': 'System Controls',
    '/logs': 'Audit Logs',
    '/feedback': 'User Feedback',
    '/testers': 'Tester Signups',
    '/data-export': 'Export My Data',
    '/delete-account': 'Delete Account',
  };

  // Handle dynamic routes
  if (pathname.startsWith('/moderation/')) return 'Moderation Case';
  if (pathname.startsWith('/prompts/new')) return 'Create Prompt';
  if (pathname.startsWith('/prompts/')) return 'Prompt Details';
  if (pathname.startsWith('/automation/personas/new')) return 'Create Persona';
  if (pathname.startsWith('/automation/personas/')) return 'Persona Details';
  if (pathname.startsWith('/automation/personas')) return 'Personas';
  if (pathname.startsWith('/automation/rules/new')) return 'Create Rule';
  if (pathname.startsWith('/automation/rules/')) return 'Rule Details';
  if (pathname.startsWith('/automation/rules')) return 'Automation Rules';
  if (pathname.startsWith('/automation/logs')) return 'Execution Logs';
  if (pathname.startsWith('/frames/new')) return 'Create Frame';
  if (pathname.startsWith('/frames/')) return 'Frame Details';
  if (pathname.startsWith('/filters/new')) return 'Create Filter';
  if (pathname.startsWith('/filters/')) return 'Filter Details';
  if (pathname.startsWith('/loading/new')) return 'Create Loading Screen';
  if (pathname.startsWith('/loading/')) return 'Loading Screen Details';
  if (pathname.startsWith('/feedback/')) return 'Feedback Detail';
  if (pathname.startsWith('/users/')) return 'User Details';

  return titles[pathname] || 'Admin';
}
